import { IPatient, PatientGender } from './patient.types';

// -----------------------------------------------------------------------------------------------------
// @ Patient Model
// -----------------------------------------------------------------------------------------------------

/**
 * Patient model class that implements the IPatient interface
 * Updated to match OpenAPI specification exactly
 * Provides computed properties for UI display and patient operations
 */
export class Patient implements Required<IPatient> {
    // Core properties from API - matching OpenAPI spec exactly
    entityId: string;                    // Required UUID
    zTouchTimestamp: string | null;      // Legacy modification timestamp (write-only)
    zCreationTimestamp: string | null;   // Legacy creation timestamp (write-only)
    firstName: string | null;
    lastName: string;                    // Required
    gender: PatientGender;              // Required
    birthDate: string;                  // Required ISO date
    birthPlace: string | null;
    address: string | null;
    city: string | null;
    country: string | null;
    phone1: string | null;
    phone2: string | null;
    email: string | null;
    occupation: string | null;
    emergencyContactName: string;       // Required
    emergencyContactPhone: string;      // Required
    emergencyContactEmail: string | null;
    ztouchTimestamp: string;            // Required normalized timestamp
    zcreationTimestamp: string;         // Required creation timestamp

    /**
     * Returns patient's full name for display
     */
    get fullName(): string {
        if (this.firstName) {
            return `${this.firstName} ${this.lastName}`;
        }
        return this.lastName;
    }

    /**
     * Returns patient's display name with title based on gender
     */
    get displayName(): string {
        const title = this.gender === 'MALE' ? 'Mr.' : 
                     this.gender === 'FEMALE' ? 'Ms.' : '';
        return title ? `${title} ${this.fullName}` : this.fullName;
    }

    /**
     * Returns patient's initials for avatar display
     */
    get initials(): string {
        const firstInitial = this.firstName ? this.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = this.lastName.charAt(0).toUpperCase();
        return firstInitial ? `${firstInitial}${lastInitial}` : lastInitial;
    }

    /**
     * Returns patient's age calculated from birth date (in years)
     */
    get age(): number {
        if (!this.birthDate) return 0;

        const today = new Date();
        const birthDate = new Date(this.birthDate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return Math.max(0, age);
    }

    /**
     * Returns patient's age in months (for babies under 1 year)
     */
    get ageInMonths(): number {
        if (!this.birthDate) return 0;

        const today = new Date();
        const birthDate = new Date(this.birthDate);

        let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
        months += today.getMonth() - birthDate.getMonth();

        // Subtract one month if we haven't reached the birth day yet this month
        if (today.getDate() < birthDate.getDate()) {
            months--;
        }

        return Math.max(0, months);
    }

    /**
     * Returns patient's age in days (for very young babies)
     */
    get ageInDays(): number {
        if (!this.birthDate) return 0;

        const today = new Date();
        const birthDate = new Date(this.birthDate);

        const diffTime = today.getTime() - birthDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return Math.max(0, diffDays);
    }

    /**
     * Returns formatted age string suitable for display
     * - For babies < 7 days: "Âge : X jours"
     * - For babies < 2 months: "Âge : X semaines"
     * - For babies < 24 months: "Âge : X mois"
     * - For older: "Âge : X an" or "Âge : X ans"
     */
    get ageFormatted(): string {
        if (!this.birthDate) return 'Âge inconnu';

        const days = this.ageInDays;
        const months = this.ageInMonths;
        const years = this.age;

        // Less than 7 days: show days
        if (days < 7) {
            const ageText = days === 0 ? 'Nouveau-né' : days === 1 ? '1 jour' : `${days} jours`;
            return `Âge : ${ageText}`;
        }

        // Less than 8 weeks (56 days): show weeks
        if (days < 56) {
            const weeks = Math.floor(days / 7);
            const ageText = weeks === 1 ? '1 semaine' : `${weeks} semaines`;
            return `Âge : ${ageText}`;
        }

        // Less than 24 months: show months
        if (months < 24) {
            const ageText = months === 1 ? '1 mois' : `${months} mois`;
            return `Âge : ${ageText}`;
        }

        // 24 months and above: show years
        const ageText = years === 1 ? '1 an' : `${years} ans`;
        return `Âge : ${ageText}`;
    }

    /**
     * Returns a short formatted age string for compact displays
     * Examples: "5j", "3sem", "8m", "25a"
     */
    get ageShort(): string {
        if (!this.birthDate) return '-';

        const days = this.ageInDays;
        const months = this.ageInMonths;
        const years = this.age;

        if (days < 7) {
            return days === 0 ? 'NN' : `${days}j`;
        }

        if (days < 56) {
            const weeks = Math.floor(days / 7);
            return `${weeks}sem`;
        }

        if (months < 24) {
            return `${months}m`;
        }

        return `${years}a`;
    }

    /**
     * Returns formatted birth date for display
     */
    get birthDateFormatted(): string {
        if (!this.birthDate) return 'Unknown';
        
        try {
            return new Date(this.birthDate).toLocaleDateString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns primary contact information
     */
    get primaryContact(): string {
        return this.phone1;
    }

    /**
     * Returns all contact methods available
     */
    get contactMethods(): string[] {
        const contacts: string[] = [];
        
        if (this.phone1) contacts.push(this.phone1);
        if (this.phone2) contacts.push(this.phone2);
        if (this.email) contacts.push(this.email);
        
        return contacts;
    }

    /**
     * Returns full address string
     */
    get fullAddress(): string {
        const parts: string[] = [];
        
        if (this.address) parts.push(this.address);
        if (this.city) parts.push(this.city);
        if (this.country) parts.push(this.country);
        
        return parts.join(', ');
    }

    /**
     * Returns emergency contact information
     */
    get emergencyContact(): string {
        const parts: string[] = [this.emergencyContactName];
        if (this.emergencyContactPhone) parts.push(this.emergencyContactPhone);
        
        return parts.join(' - ');
    }

    /**
     * Returns gender display name
     */
    get genderDisplay(): string {
        switch (this.gender) {
            case 'MALE':
                return 'Male';
            case 'FEMALE':
                return 'Female';
            case 'OTHER':
                return 'Other';
            default:
                return this.gender;
        }
    }

    /**
     * Returns creation date formatted for display
     */
    get createdAt(): string {
        return this.zcreationTimestamp || new Date().toISOString();
    }

    /**
     * Returns last modified date formatted for display
     */
    get modifiedAt(): string {
        return this.ztouchTimestamp || new Date().toISOString();
    }

    /**
     * Returns formatted creation date
     */
    get createdAtFormatted(): string {
        if (!this.zcreationTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.zcreationTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns formatted modification date
     */
    get modifiedAtFormatted(): string {
        if (!this.ztouchTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.ztouchTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns the primary ID (entityId for API compatibility)
     */
    get id(): string {
        return this.entityId;
    }

    /**
     * Constructor - initializes Patient from API response
     * Updated to match OpenAPI specification exactly
     */
    constructor(patient: IPatient) {
        // Required fields
        this.entityId = patient.entityId || '';
        this.lastName = patient.lastName;
        this.gender = patient.gender;
        this.birthDate = patient.birthDate;
        this.city = patient.city;
        this.country = patient.country;
        this.phone1 = patient.phone1;
        this.emergencyContactName = patient.emergencyContactName || '';
        this.emergencyContactPhone = patient.emergencyContactPhone || '';
        this.ztouchTimestamp = patient.ztouchTimestamp || new Date().toISOString();
        this.zcreationTimestamp = patient.zcreationTimestamp || new Date().toISOString();
        
        // Optional fields
        this.zTouchTimestamp = patient.zTouchTimestamp || null;
        this.zCreationTimestamp = patient.zCreationTimestamp || null;
        this.firstName = patient.firstName || null;
        this.birthPlace = patient.birthPlace || null;
        this.address = patient.address || null;
        this.phone2 = patient.phone2 || null;
        this.email = patient.email || null;
        this.occupation = patient.occupation || null;
        this.emergencyContactEmail = patient.emergencyContactEmail || null;
    }

    /**
     * Converts patient model to DTO for API requests
     */
    toDTO(): IPatient {
        return {
            entityId: this.entityId,
            zTouchTimestamp: this.zTouchTimestamp,
            zCreationTimestamp: this.zCreationTimestamp,
            firstName: this.firstName,
            lastName: this.lastName,
            gender: this.gender,
            birthDate: this.birthDate,
            birthPlace: this.birthPlace,
            address: this.address,
            city: this.city,
            country: this.country,
            phone1: this.phone1,
            phone2: this.phone2,
            email: this.email,
            occupation: this.occupation,
            emergencyContactName: this.emergencyContactName,
            emergencyContactPhone: this.emergencyContactPhone,
            emergencyContactEmail: this.emergencyContactEmail,
            ztouchTimestamp: this.ztouchTimestamp,
            zcreationTimestamp: this.zcreationTimestamp
        };
    }

    /**
     * Converts to input DTO for create/update operations
     */
    toInputDTO() {
        return {
            lastName: this.lastName,
            firstName: this.firstName,
            gender: this.gender,
            phone1: this.phone1,
            phone2: this.phone2,
            birthDate: this.birthDate,
            birthPlace: this.birthPlace,
            email: this.email,
            occupation: this.occupation,
            address: this.address,
            emergencyContactName: this.emergencyContactName,
            emergencyContactPhone: this.emergencyContactPhone,
            emergencyContactEmail: this.emergencyContactEmail,
            city: this.city,
            country: this.country
        };
    }

    /**
     * Checks if patient matches search term
     */
    matchesSearch(searchTerm: string): boolean {
        if (!searchTerm) return true;
        
        const term = searchTerm.toLowerCase();
        const searchableFields = [
            this.fullName.toLowerCase(),
            this.phone1?.toLowerCase() || '',
            this.email?.toLowerCase() || '',
            this.city?.toLowerCase() || '',
            this.occupation?.toLowerCase() || '',
            this.emergencyContactName.toLowerCase(),
            this.emergencyContactPhone.toLowerCase()
        ];
        
        return searchableFields.some(field => field.includes(term));
    }

    /**
     * Validates patient data
     */
    validate(): { isValid: boolean; errors: { [key: string]: string } } {
        const errors: { [key: string]: string } = {};

        // Required fields validation
        if (!this.lastName?.trim()) {
            errors.lastName = 'Last name is required';
        }

        if (!this.birthDate) {
            errors.birthDate = 'Birth date is required';
        } else {
            const birthDate = new Date(this.birthDate);
            const today = new Date();
            if (birthDate > today) {
                errors.birthDate = 'Birth date cannot be in the future';
            }
        }

        // Emergency contact required fields
        if (!this.emergencyContactName?.trim()) {
            errors.emergencyContactName = 'Emergency contact name is required';
        }

        if (!this.emergencyContactPhone?.trim()) {
            errors.emergencyContactPhone = 'Emergency contact phone is required';
        }

        // Email validation
        if (this.email && !this.isValidEmail(this.email)) {
            errors.email = 'Invalid email format';
        }

        // Emergency contact email validation
        if (this.emergencyContactEmail && !this.isValidEmail(this.emergencyContactEmail)) {
            errors.emergencyContactEmail = 'Invalid emergency contact email format';
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    /**
     * Email validation helper
     */
    private isValidEmail(email: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Clone patient instance
     */
    clone(): Patient {
        return new Patient(this.toDTO());
    }
}